<template>
  <span
    ref="anchor"
    class="ui-sticky-actions"
    :style="placeholderStyle"
  >
    <span
      ref="content"
      class="ui-sticky-actions__content"
      :class="{ 'is-floating': floating }"
      :style="floatingStyle"
    >
      <slot />
    </span>
  </span>
</template>

<script>
export default {
  name: 'UiStickyActions',
  props: {
    topOffset: { type: Number, default: 10 },
    boundaryOffset: { type: Number, default: 10 },
    boundarySelector: { type: String, default: '.ui-section-card, .el-dialog' },
    // 默认滚入下一块内容约三分之一屏后归位；可显式指定像素距离。
    releaseDistance: { type: Number, default: null }
  },
  data () {
    return {
      floating: false,
      returning: false,
      returnTimer: null,
      anchorWidth: 0,
      anchorHeight: 0,
      fixedTop: 0,
      fixedLeft: 0,
      floatingOpacity: 1,
      scrollTarget: null,
      frameId: 0,
      resizeObserver: null,
      actionObserver: null
    }
  },
  computed: {
    placeholderStyle () {
      if (!this.floating) return null
      return {
        width: `${this.anchorWidth}px`,
        height: `${this.anchorHeight}px`
      }
    },
    floatingStyle () {
      if (!this.floating) return null
      return {
        top: `${this.fixedTop}px`,
        left: `${this.fixedLeft}px`,
        opacity: this.floatingOpacity
      }
    }
  },
  mounted () {
    this.$nextTick(this.bindPositionTracking)
  },
  updated () {
    this.schedulePositionUpdate()
  },
  beforeDestroy () {
    this.unbindPositionTracking()
  },
  methods: {
    findScrollParent (element) {
      let node = element && element.parentElement
      while (node && node !== document.body) {
        const style = window.getComputedStyle(node)
        // 隐藏的弹窗和尚未撑满的区域也有自己的滚动视口，不能按当前溢出量判断。
        if (/(auto|scroll|overlay)/.test(style.overflowY)) return node
        node = node.parentElement
      }
      return window
    },
    syncScrollTarget () {
      const nextTarget = this.findScrollParent(this.$refs.anchor)
      if (nextTarget === this.scrollTarget) return
      if (this.scrollTarget) this.scrollTarget.removeEventListener('scroll', this.schedulePositionUpdate)
      this.scrollTarget = nextTarget
      this.scrollTarget.addEventListener('scroll', this.schedulePositionUpdate, { passive: true })
      clearTimeout(this.returnTimer)
      this.returnTimer = null
      this.returning = false
      this.floating = false
    },
    bindPositionTracking () {
      this.syncScrollTarget()
      window.addEventListener('scroll', this.schedulePositionUpdate, { passive: true, capture: true })
      window.addEventListener('resize', this.schedulePositionUpdate, { passive: true })
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.schedulePositionUpdate)
        const boundary = this.getBoundary()
        if (boundary) this.resizeObserver.observe(boundary)
      }
      if (typeof MutationObserver !== 'undefined') {
        this.actionObserver = new MutationObserver(this.schedulePositionUpdate)
        this.actionObserver.observe(this.$refs.content, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-ui-action', 'class', 'style']
        })
      }
      this.schedulePositionUpdate()
    },
    unbindPositionTracking () {
      clearTimeout(this.returnTimer)
      if (this.scrollTarget) this.scrollTarget.removeEventListener('scroll', this.schedulePositionUpdate)
      window.removeEventListener('resize', this.schedulePositionUpdate)
      window.removeEventListener('scroll', this.schedulePositionUpdate, true)
      if (this.resizeObserver) this.resizeObserver.disconnect()
      if (this.actionObserver) this.actionObserver.disconnect()
      if (this.frameId) cancelAnimationFrame(this.frameId)
      this.frameId = 0
    },
    getBoundary () {
      const anchor = this.$refs.anchor
      return anchor && this.boundarySelector ? anchor.closest(this.boundarySelector) : null
    },
    getViewportTop () {
      if (!this.scrollTarget || this.scrollTarget === window) return 0
      return this.scrollTarget.getBoundingClientRect().top
    },
    schedulePositionUpdate () {
      if (this.frameId) return
      this.frameId = requestAnimationFrame(() => {
        this.frameId = 0
        this.updatePosition()
      })
    },
    updatePosition () {
      const anchor = this.$refs.anchor
      const content = this.$refs.content
      const boundary = this.getBoundary()
      if (!anchor || !content || !boundary) return

      // 与 UiButton 的实色提交语义一致；禁用或保存中仍保留整组跟随。
      const hasSubmit = Array.from(content.querySelectorAll(
        '.ui-button--standard.ui-button--tone-primary[data-ui-action="create"], ' +
        '.ui-button--standard.ui-button--tone-primary[data-ui-action="save"], ' +
        '.ui-button--standard.ui-button--tone-primary[data-ui-action="confirm"]'
      )).some(button => button.getClientRects().length && window.getComputedStyle(button).visibility !== 'hidden')

      // 弹窗动画会移动整个 wrapper，动画期间的视口坐标不能用于吸附判断。
      const dialogWrapper = anchor.closest('.el-dialog__wrapper')
      const dialogTransitioning = dialogWrapper && (
        dialogWrapper.classList.contains('dialog-fade-enter-active') ||
        dialogWrapper.classList.contains('dialog-fade-leave-active')
      )
      const visible = Boolean(anchor.getClientRects().length)
      if (!hasSubmit || !visible || dialogTransitioning) {
        clearTimeout(this.returnTimer)
        this.returnTimer = null
        this.returning = false
        this.floating = false
        if (visible && dialogTransitioning) this.schedulePositionUpdate()
        return
      }

      // append-to-body 或布局变化后，必须按当前 DOM 重新确认滚动视口。
      this.syncScrollTarget()
      const anchorRect = anchor.getBoundingClientRect()
      const boundaryRect = boundary.getBoundingClientRect()
      const viewportTop = this.getViewportTop()
      const stickyTop = viewportTop + this.topOffset
      const viewportBottom = this.scrollTarget && this.scrollTarget !== window
        ? Math.min(window.innerHeight, this.scrollTarget.getBoundingClientRect().bottom)
        : window.innerHeight
      // 16px 滞回防止临界位置滚动时反复吸附、归位。
      const viewportHeight = Math.max(0, viewportBottom - viewportTop)
      const releaseDistance = this.releaseDistance == null
        ? viewportHeight / 3
        : Math.max(0, this.releaseDistance)
      const releaseLine = viewportBottom - releaseDistance + (this.floating ? 0 : 16)

      // 向上滚动到原位置时立即交回正常布局，不能等待归位动画。
      if (anchorRect.top >= stickyTop) {
        clearTimeout(this.returnTimer)
        this.returnTimer = null
        this.returning = false
        this.floating = false
        return
      }

      if (boundaryRect.bottom < releaseLine || boundaryRect.bottom <= stickyTop) {
        if (!this.floating || this.returning) return
        // 向下滚出分区时保留滑出动画，完成后再恢复正常布局。
        this.returning = true
        this.fixedTop = Math.max(anchorRect.top, viewportTop - this.anchorHeight - 12)
        this.fixedLeft = anchorRect.left
        this.floatingOpacity = 1
        this.returnTimer = setTimeout(() => {
          this.returning = false
          this.floating = false
          this.returnTimer = null
        }, 240)
        return
      }

      if (this.returning) {
        clearTimeout(this.returnTimer)
        this.returnTimer = null
        this.returning = false
      }

      if (!this.floating) {
        const contentRect = content.getBoundingClientRect()
        this.anchorWidth = contentRect.width
        this.anchorHeight = contentRect.height
        this.floating = true
      }

      const constrainedTop = boundaryRect.bottom - this.boundaryOffset - this.anchorHeight
      const nextTop = Math.min(stickyTop, constrainedTop)
      const visibleHeight = nextTop + this.anchorHeight - viewportTop

      this.fixedTop = nextTop
      this.fixedLeft = anchorRect.left
      this.floatingOpacity = Math.max(0, Math.min(1, visibleHeight / Math.max(this.anchorHeight, 1)))
    }
  }
}
</script>

<style lang="scss" scoped>
.ui-sticky-actions {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  vertical-align: middle;
}

.ui-sticky-actions__content {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--ui-control-gap);
  white-space: nowrap;
}

.ui-sticky-actions__content.is-floating {
  position: fixed;
  z-index: 40;
  border-radius: var(--ui-radius-medium);
  background: rgba(var(--ui-bg-elevated-rgb), .94);
  box-shadow:
    0 0 0 var(--ui-space-xs) rgba(var(--ui-bg-elevated-rgb), .94),
    var(--ui-shadow-surface);
  box-sizing: border-box;
  transition:
    top 220ms cubic-bezier(.22, .61, .36, 1),
    left 180ms cubic-bezier(.22, .61, .36, 1),
    opacity 140ms ease;
  will-change: top, left, opacity;
}

.ui-sticky-actions__content ::v-deep .el-button + .el-button {
  margin-left: 0;
}
</style>
